import React, { useState, useEffect } from 'react';
import { API, COLORS } from '../utils/constants';
import { r1 } from '../utils/helpers';
import DaySelector from '../components/DaySelector';
import QuickFoodCard from '../components/QuickFoodCard';
import CircularProgress from '../components/CircularProgress';
import Popup from '../components/Popup';
import BarraSemaforo from '../components/BarraSemaforo';


const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DAY_LABELS = ["L","M","X","J","V","S","D"];

// Color dinámico según calorías ingeridas vs quemadas
function getCalColor(calIn, burned) {
  if (!burned) return COLORS.accent; // sin datos → naranja por defecto
  if (calIn > burned + 100) return "#ff2d55";  // rojo: exceso > 100
  if (calIn < burned - 100) return "#f9c74f";  // amarillo: déficit > 100
  return COLORS.green;                          // verde: equilibrio
}

// Color dinámico según pasos
function getStepsColor(pasos) {
  if (!pasos) return COLORS.textMuted;
  if (pasos < 10000) return "#ff2d55";   // rojo
  if (pasos <= 20000) return "#ff9500";  // naranja
  return COLORS.green;                   // verde
}

export default function HomeScreen({ todayType, setTodayType, nutrition, setNutrition, setTab, currentUser }) {
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];
  const stepsGoal = 20000;

  const [healthData, setHealthData] = useState({ pasos: 0, calorias_activas: 0, calorias_reposo: 0 });

  // Fecha seleccionada (null = hoy)
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHealthData, setSelectedHealthData] = useState(null);
  const [selectedCalIn, setSelectedCalIn] = useState(null);

  // Calendario
  const [showCalendar, setShowCalendar] = useState(false);
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  // Datos a mostrar según fecha seleccionada
  const displayHealth = selectedDate ? (selectedHealthData || { pasos: 0, calorias_activas: 0, calorias_reposo: 0 }) : healthData;
  const displayCalIn = selectedDate !== null ? (selectedCalIn || 0) : nutrition.calories.current;

  // ── Lógica de colores dinámicos ──────────────────────────────────────────
  // Apple Watch card: usa datos de la fecha mostrada (puede ser histórica)
  const displayBurned = displayHealth.calorias_activas
    ? Math.round(displayHealth.calorias_activas + displayHealth.calorias_reposo)
    : 0;
  const watchCalColor = getCalColor(displayCalIn, displayBurned);
  const stepsCol = getStepsColor(displayHealth.pasos);

  // Macros card: siempre usa datos de HOY (la tarjeta se llama "Macros de hoy")
  const todayBurned = healthData.calorias_activas
    ? Math.round(healthData.calorias_activas + healthData.calorias_reposo)
    : 0;
  const macroCalColor = getCalColor(nutrition.calories.current, todayBurned);
  const macroCalMax = todayBurned || nutrition.calories.goal;
  const calOverflowPct = todayBurned > 0 && nutrition.calories.current > todayBurned
    ? (nutrition.calories.current - todayBurned) / todayBurned
    : 0;
  // ─────────────────────────────────────────────────────────────────────────

  const fetchHealthData = () => {
    fetch(API + "/health-sync/" + todayStr, { credentials: "include" })
      .then(r => r.json())
      .then(data => setHealthData(data))
      .catch(() => {});
  };

  const fetchForDate = (fecha) => {
    Promise.all([
      fetch(API + "/health-sync/" + fecha, { credentials: "include" }).then(r => r.json()).catch(() => ({ pasos: 0, calorias_activas: 0, calorias_reposo: 0 })),
      fetch(API + "/food-log/" + fecha, { credentials: "include" }).then(r => r.json()).catch(() => []),
    ]).then(([health, foods]) => {
      setSelectedHealthData(health);
      setSelectedCalIn((foods || []).reduce((s, f) => s + (Number(f.calorias) || 0), 0));
    });
  };

  useEffect(() => {
    fetchHealthData();
  }, []);

  // Refresh Apple Health data when returning to home tab
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        fetchHealthData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const hour = today.getHours();
  const greeting = hour >= 6 && hour < 14 ? "Buenos días" : hour >= 14 && hour < 20 ? "Buenas tardes" : "Buenas noches";

  const formatDateLabel = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  };

  // Navegación del calendario
  const calPrevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const calNextMonth = () => {
    const nextY = calMonth === 11 ? calYear + 1 : calYear;
    const nextM = calMonth === 11 ? 0 : calMonth + 1;
    if (nextY > today.getFullYear() || (nextY === today.getFullYear() && nextM > today.getMonth())) return;
    setCalMonth(nextM); if (calMonth === 11) setCalYear(y => y + 1);
  };
  const canGoNext = !(calYear === today.getFullYear() && calMonth === today.getMonth());

  const buildCalendarDays = () => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const getDayStr = (d) => `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const isDaySelectable = (d) => {
    const s = getDayStr(d);
    return s >= "2026-01-01" && s <= todayStr;
  };

  const selectDay = (d) => {
    if (!isDaySelectable(d)) return;
    const s = getDayStr(d);
    setSelectedDate(s);
    fetchForDate(s);
    setShowCalendar(false);
  };

  const resetToToday = () => {
    setSelectedDate(null);
    setSelectedHealthData(null);
    setSelectedCalIn(null);
    setCalYear(today.getFullYear());
    setCalMonth(today.getMonth());
  };

  const calDays = buildCalendarDays();

  return (
    <div style={{ padding: "0 16px 90px", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <div style={{ paddingTop: 'calc(var(--sat, env(safe-area-inset-top, 0px)) + 14px)', paddingBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <p style={{ color: COLORS.accent, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 4px", fontWeight: 800 }}>
            {today.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 style={{ color: "#fff", fontSize: 28, fontWeight: 900, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -1, lineHeight: 1.1 }}>
            {greeting} <span style={{ background: `linear-gradient(90deg, ${COLORS.accent}, #ff3366)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{(currentUser?.nombre || currentUser?.username || 'tú')}.</span>
          </h1>
          {currentUser?.handle && <p style={{ color: COLORS.textMuted, fontSize: 11, margin: '6px 0 0', letterSpacing: 1 }}>@{currentUser.handle}</p>}
        </div>
      </div>

      {/* Fila: Sesión de hoy + FitCoach */}
      <div style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "stretch" }}>
        <div style={{ flex: "0 0 46%", display: "flex", flexDirection: "column" }}>
          <DaySelector todayType={todayType} setTodayType={setTodayType} setTab={setTab} />
        </div>
        <div
          onClick={() => setTab('coach')}
          style={{
            flex: 1,
            background: `linear-gradient(135deg, ${COLORS.blue}18, rgba(28,27,27,0.6))`,
            backdropFilter: "blur(30px)", WebkitBackdropFilter: "blur(30px)",
            border: `1px solid ${COLORS.blue}40`,
            borderRadius: 20,
            cursor: "pointer",
            boxShadow: `0 8px 24px ${COLORS.blue}15`,
            display: "flex", flexDirection: "column",
            justifyContent: "center", alignItems: "center",
            gap: 5, padding: "12px 8px",
          }}
        >
          <span style={{ fontSize: 26 }}>🤖</span>
          <span style={{ color: COLORS.blue, fontSize: 14, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -0.3 }}>FitCoach</span>
          <span style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", textAlign: "center" }}>tu entrenador IA</span>
        </div>
      </div>

      {/* Tarjeta Apple Watch */}
      <div
        onClick={!selectedDate ? fetchHealthData : undefined}
        style={{ position: "relative", overflow: "hidden", background: "rgba(30, 30, 35, 0.4)", backdropFilter: "blur(30px)", borderRadius: 24, padding: "10px 14px", marginBottom: 10, border: `1px solid rgba(255,255,255,0.08)`, cursor: !selectedDate ? "pointer" : "default", boxShadow: `0 12px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)` }}
      >
        <div style={{ position: "absolute", top: "-50%", left: "-50%", width: "200%", height: "200%", background: `radial-gradient(circle at center, ${watchCalColor}15 0%, transparent 60%)`, pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "relative", zIndex: 1 }}>
        {/* Cabecera unificada */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -0.5, display: "flex", alignItems: "center", gap: 6 }}>
            ⌚ Apple Watch
            <button
              onClick={e => { e.stopPropagation(); setCalYear(selectedDate ? parseInt(selectedDate.split("-")[0]) : today.getFullYear()); setCalMonth(selectedDate ? parseInt(selectedDate.split("-")[1]) - 1 : today.getMonth()); setShowCalendar(true); }}
              style={{ background: "none", border: "none", color: selectedDate ? COLORS.accent : COLORS.textMuted, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", padding: 0, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3, marginLeft: 4 }}
            >
              {selectedDate ? formatDateLabel(selectedDate) : "HOY"}
            </button>
          </h2>
          {selectedDate && (
            <button
              onClick={e => { e.stopPropagation(); resetToToday(); }}
              style={{ background: COLORS.accentSoft, border: `1px solid ${COLORS.accent}50`, borderRadius: 12, padding: "4px 10px", color: COLORS.accent, fontSize: 10, fontWeight: 800, cursor: "pointer", letterSpacing: 1 }}
            >
              HOY
            </button>
          )}
        </div>

        {/* Grid de datos */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1fr", gap: 8 }}>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: "10px 4px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", border: `1px solid rgba(255,255,255,0.03)`, boxShadow: `0 4px 8px rgba(0,0,0,0.2)` }}>
              <span style={{ fontSize: 20, marginBottom: 2 }}>👟</span>
              <span style={{ color: stepsCol, fontSize: 16, fontWeight: 900, letterSpacing: -0.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{displayHealth.pasos ? displayHealth.pasos.toLocaleString('es-ES') : "--"}</span>
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, letterSpacing: 0.5, marginTop: 2, fontWeight: 700 }}>PASOS</span>
            </div>

            <div style={{ background: `linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))`, borderRadius: 16, padding: "10px 4px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 6, border: `1px solid ${watchCalColor}50`, boxShadow: `0 4px 12px ${watchCalColor}30` }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: watchCalColor, fontSize: 16, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif", textShadow: `0 0 10px ${watchCalColor}50` }}>
                  {r1(displayCalIn)}<span style={{ fontSize: 9, fontWeight: 700 }}>kcal</span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, letterSpacing: 0.5, marginTop: 1, fontWeight: 700 }}>INGERIDAS</div>
              </div>
              <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${watchCalColor}60, transparent)`, margin: "0 6px" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {displayHealth.calorias_activas ? Math.round(displayHealth.calorias_activas + displayHealth.calorias_reposo) : "--"}
                  <span style={{ fontSize: 9, fontWeight: 700 }}>kcal</span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, letterSpacing: 0.5, marginTop: 1, fontWeight: 700 }}>QUEMADAS</div>
              </div>
            </div>

            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: "10px 4px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 6, border: `1px solid rgba(255,255,255,0.03)`, boxShadow: `0 4px 8px rgba(0,0,0,0.2)` }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: COLORS.accent, fontSize: 14, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {displayHealth.calorias_activas ? Math.round(displayHealth.calorias_activas) : "--"}
                </div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, letterSpacing: 0.5, marginTop: 1, fontWeight: 700 }}>ACTIVA</div>
              </div>
              <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "0 6px" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ color: COLORS.blue, fontSize: 14, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {displayHealth.calorias_reposo ? Math.round(displayHealth.calorias_reposo) : "--"}
                </div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, letterSpacing: 0.5, marginTop: 1, fontWeight: 700 }}>REPOSO</div>
              </div>
            </div>
          </div>

        {/* Progreso pasos — color dinámico */}
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>Progreso de Pasos</span>
            <span style={{ color: stepsCol, fontSize: 10, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {displayHealth.pasos ? Math.min(100, Math.round(displayHealth.pasos / stepsGoal * 100)) : 0}%
            </span>
          </div>
          <div style={{ background: "rgba(0,0,0,0.6)", borderRadius: 6, height: 6, overflow: "hidden", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.8)" }}>
            <div style={{ height: "100%", borderRadius: 6, background: `linear-gradient(90deg, ${stepsCol}60, ${stepsCol})`, width: `${displayHealth.pasos ? Math.min(100, displayHealth.pasos / stepsGoal * 100) : 0}%`, transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)", boxShadow: `0 0 10px ${stepsCol}80` }} />
          </div>
        </div>

        {/* Barra semáforo — balance calórico */}
        <BarraSemaforo calIn={displayCalIn} burned={displayBurned} style={{ marginTop: 6 }} />
        </div>
      </div>

      {/* Macros de hoy */}
      <div style={{ background: "rgba(30, 30, 35, 0.4)", backdropFilter: "blur(20px)", borderRadius: 24, padding: "10px 14px", border: `1px solid rgba(255,255,255,0.05)`, marginBottom: 10, display: "flex", flexDirection: "column", boxShadow: "0 8px 16px rgba(0,0,0,0.3)" }}>
        <h2 style={{ color: "#fff", fontSize: 14, fontWeight: 800, margin: "0 0 8px", fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -0.5 }}>Macros</h2>
        <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
          <CircularProgress value={r1(nutrition.calories.current)} max={macroCalMax} color={macroCalColor} size={56} strokeWidth={6} label="Kcal" overflowPct={calOverflowPct} />
          <CircularProgress value={r1(nutrition.protein.current)} max={nutrition.protein.goal} color={COLORS.blue} size={44} strokeWidth={5} label="Prot" sublabel="g" />
          <CircularProgress value={r1(nutrition.carbs.current)} max={nutrition.carbs.goal} color={COLORS.green} size={44} strokeWidth={5} label="Carb" sublabel="g" />
          <CircularProgress value={r1(nutrition.fat.current)} max={nutrition.fat.goal} color={COLORS.purple} size={44} strokeWidth={5} label="Grasa" sublabel="g" />
        </div>
      </div>

      <QuickFoodCard nutrition={nutrition} setNutrition={setNutrition} />

      {/* Modal Calendario */}
      {showCalendar && (
        <Popup type="centered" onClose={() => setShowCalendar(false)} zIndex={700}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <button onClick={calPrevMonth} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 12, width: 40, height: 40, color: "#fff", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 16, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{MONTH_NAMES[calMonth]} {calYear}</span>
              <button onClick={calNextMonth} disabled={!canGoNext} title={canGoNext ? "Mes siguiente" : "No hay datos futuros"} style={{ background: canGoNext ? "rgba(255,255,255,0.05)" : "transparent", border: `1px solid ${canGoNext ? "rgba(255,255,255,0.1)" : "transparent"}`, borderRadius: 12, width: 40, height: 40, color: canGoNext ? "#fff" : "rgba(255,255,255,0.2)", fontSize: 18, cursor: canGoNext ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 12 }}>
              {DAY_LABELS.map(d => ( <div key={d} style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 800, letterSpacing: 1 }}>{d}</div> ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
              {calDays.map((d, i) => {
                if (!d) return <div key={i} />;
                const dStr = getDayStr(d);
                const selectable = isDaySelectable(d);
                const isSelected = selectedDate === dStr;
                const isToday = dStr === todayStr;
                return (
                  <button key={i} onClick={() => selectDay(d)} disabled={!selectable}
                    style={{ width: "100%", aspectRatio: "1", borderRadius: 14, border: "none", background: isSelected ? COLORS.accent : isToday ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.2)", color: !selectable ? "rgba(255,255,255,0.2)" : isSelected ? "#fff" : isToday ? COLORS.accent : "#fff", fontSize: 14, fontWeight: isSelected || isToday ? 800 : 600, cursor: selectable ? "pointer" : "default", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >{d}</button>
                );
              })}
            </div>
            <button onClick={() => setShowCalendar(false)} style={{ marginTop: 24, width: "100%", padding: 16, borderRadius: 16, background: "rgba(255,255,255,0.05)", border: `1px solid rgba(255,255,255,0.1)`, color: "#fff", cursor: "pointer", fontWeight: 800, fontSize: 15, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Cancelar</button>
        </Popup>
      )}
    </div>
  );
}
